// Client-triggered, idempotent migrations for the Kiosk/Display portal split
// and the move of the Display (OOH) screen config from localStorage to the
// server. Triggered the least-disruptive way: only when a Display screen loads
// (DisplayLandingRedirect), never for ordinary users.
//
// TEMPORARY COMPAT — see docs/SCREEN_PORTAL_MIGRATION.md for what is supported
// during the transition and when these shims can be removed.
import {
  cacheDisplayConfig,
  fetchDisplayConfigFromServer,
  readCachedDisplayConfig,
  readLegacyDisplayConfigFromKioskConfig,
  saveDisplayConfigToServer,
} from './displayScreenConfig'
import {
  cacheKioskConfig,
  fetchKioskConfigFromServer,
  readCachedKioskConfig,
  readLegacyKioskConfigFromLocal,
  saveKioskConfigToServer,
} from './kioskScreenConfig'

const DISPLAY_MIGRATION_FLAG = 'displayConfigMigratedToServerV1'
const KIOSK_MIGRATION_FLAG = 'kioskConfigMigratedToServerV1'

function kioskMigrationFlagKey(cfg) {
  return cfg?.screenDeviceConfigId
    ? `${KIOSK_MIGRATION_FLAG}:${cfg.screenDeviceConfigId}`
    : KIOSK_MIGRATION_FLAG
}

/**
 * One-time migration: push a Display screen's legacy localStorage OOH target
 * (kiosk-config / ROLE_CA_CONFIG with LOCATION_FOR_OOH + MENTOR_FOR_OOH) to the
 * server ScreenAssignment. Idempotent and best-effort:
 *  - skips if already migrated (flag set),
 *  - no-ops (and flags done) if the server already has a config or there's
 *    nothing legacy to migrate,
 *  - on network failure it does NOT set the flag, so it retries next load.
 * Returns the migrated config, or null.
 */
export async function migrateLegacyDisplayConfigToServer() {
  try {
    if (localStorage.getItem(DISPLAY_MIGRATION_FLAG) === 'done') return null

    let serverCfg = null
    try {
      serverCfg = await fetchDisplayConfigFromServer()
    } catch {
      return null // offline — retry on a later load (flag stays unset)
    }
    if (serverCfg) {
      // Server is already the source of truth; nothing to migrate.
      localStorage.setItem(DISPLAY_MIGRATION_FLAG, 'done')
      return null
    }

    const legacy = readLegacyDisplayConfigFromKioskConfig()
    if (!legacy) {
      localStorage.setItem(DISPLAY_MIGRATION_FLAG, 'done')
      return null
    }

    const res = await saveDisplayConfigToServer(legacy)
    if (res?.status === 200) {
      localStorage.setItem(DISPLAY_MIGRATION_FLAG, 'done')
      return legacy
    }
    return null // save failed — leave flag unset so it retries
  } catch {
    return null
  }
}

/**
 * Resolve which OOH screen a Display device should show, running the migration
 * as a side effect. Order: server (source of truth) → migrate legacy local
 * config to server → write-through cache fallback when offline. Returns
 * { clinicLocationId, mentorId } or null (→ caller routes to config page).
 */
export async function resolveDisplayConfig() {
  try {
    const serverCfg = await fetchDisplayConfigFromServer()
    if (serverCfg) {
      cacheDisplayConfig(serverCfg)
      localStorage.setItem(DISPLAY_MIGRATION_FLAG, 'done')
      return serverCfg
    }
    // No server config — migrate any legacy local config to the server.
    const migrated = await migrateLegacyDisplayConfigToServer()
    if (migrated) {
      cacheDisplayConfig(migrated)
      return migrated
    }
    return null
  } catch {
    // Network/transport failure — fall back to the write-through cache (or a
    // legacy local config) so an unattended screen survives a reload.
    return readCachedDisplayConfig() || readLegacyDisplayConfigFromKioskConfig()
  }
}

/**
 * One-time migration: push a Kiosk (chamber) screen's legacy localStorage target
 * (kiosk-config / ROLE_CA_CONFIG with SCREEN_DEVICE_CONFIG_ID +
 * CURRENT_VIRTUAL_ROOM_ID) to the server ScreenAssignment. Idempotent and
 * best-effort, with the same semantics as the Display migration:
 *  - skips if already migrated (flag set),
 *  - no-ops (and flags done) if the server already has a config or there's
 *    nothing legacy to migrate,
 *  - on network failure it does NOT set the flag, so it retries next load.
 * Returns the migrated config, or null.
 */
export async function migrateLegacyKioskConfigToServer(candidateConfig = null) {
  try {
    const legacy = candidateConfig || readLegacyKioskConfigFromLocal()
    const flagKey = kioskMigrationFlagKey(legacy)
    if (!candidateConfig && localStorage.getItem(flagKey) === 'done') return null

    let serverCfg = null
    try {
      serverCfg = await fetchKioskConfigFromServer(legacy?.screenDeviceConfigId)
    } catch {
      return null // offline — retry on a later load (flag stays unset)
    }
    if (serverCfg) {
      // Server is already the source of truth; nothing to migrate.
      localStorage.setItem(flagKey, 'done')
      return null
    }

    if (!legacy) {
      localStorage.setItem(KIOSK_MIGRATION_FLAG, 'done')
      return null
    }

    const res = await saveKioskConfigToServer(legacy)
    if (res?.status === 200) {
      localStorage.setItem(flagKey, 'done')
      return legacy
    }
    return null // save failed — leave flag unset so it retries
  } catch {
    return null
  }
}

/**
 * Resolve which chamber a Kiosk device should show, running the migration as a
 * side effect. Order: server (source of truth) → migrate legacy local config to
 * server → write-through cache fallback when offline. Returns
 * { screenDeviceConfigId, virtualChamberRoomId } or null (→ caller routes to
 * the config page).
 */
export async function resolveKioskConfig() {
  const localCfg = readCachedKioskConfig() || readLegacyKioskConfigFromLocal()
  try {
    const serverCfg = await fetchKioskConfigFromServer(localCfg?.screenDeviceConfigId)
    if (serverCfg) {
      cacheKioskConfig(serverCfg)
      localStorage.setItem(kioskMigrationFlagKey(serverCfg), 'done')
      return serverCfg
    }
    // No server config — migrate any legacy local config to the server.
    const migrated = await migrateLegacyKioskConfigToServer(localCfg)
    if (migrated) {
      cacheKioskConfig(migrated)
      return migrated
    }
    return null
  } catch {
    // Network/transport failure — fall back to the write-through cache (or a
    // legacy local config) so an unattended screen survives a reload.
    return localCfg
  }
}
